<script setup>
import {computed} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useToastsStore} from '@/stores/toasts'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useUserStore} from '@/stores/user'
import {useModalsStore} from '@/stores/modals'
import {useSiteTreeStore} from '@/stores/siteTree'
import {useViewStore} from '@/stores/view'
import {useAppStore} from '@/stores/appStore'

import Button from '@/common/Button.vue'
import api from '@/services/api'

const props = defineProps({
    viewId: String,
    showText: Boolean
})

const appStore = useAppStore()
const viewStore = useViewStore(props.viewId)
const componentsStore = useComponentsStore()
const toastsStore = useToastsStore()
const pageContentDataStore = usePageContentDataStore()
const userStore = useUserStore()
const modalsStore = useModalsStore()
const siteTreeStore = useSiteTreeStore('siteTree')

const tableName = computed(() => viewStore.tableName)
const selectedIds = computed(() => viewStore.selectedIds || [])
const allSelected = computed(() => viewStore.allSelected)
const allItemsSelected = computed(() => viewStore.allItemsSelected)
const itemCount = computed(() => viewStore.itemCount || 0)
const showArchive = computed(() => viewStore.showArchive || false)
const homePageId = computed(() => appStore.settings?.homePage)

const homePageIsSelected = computed(() => {
    return (
        tableName.value === 'pages' &&
        homePageId.value &&
        selectedIds.value.indexOf(homePageId.value) > -1
    )
})

const component = computed(() => componentsStore.items.find(o => o.tableName === tableName.value) || {})
const useArchive = computed(() => component.value.useArchive)
const hasArchiveAccess = computed(() => tableName.value ? userStore.access(tableName.value, 'archive') : false)

const show = computed(() => {
    return (
        useArchive.value &&
        hasArchiveAccess.value &&
        selectedIds.value.length > 0 &&
        !homePageIsSelected.value
    )
})

const text = computed(() => {
    let action = showArchive.value ? 'Un-archive' : 'Archive'
    let count = selectedIds.value.length
    return props.showText
        ? action + ' ' + count + ' item' + (count > 1 ? 's' : '')
        : ''
})

const archiveSelectItems = () => {
    if (
        tableName.value === 'pages' &&
        selectedIds.value.indexOf(homePageId.value) > -1
    ) {
        toastsStore.add({
            body: 'The home page cannot be archived.'
        })
        return false
    }

    if (selectedIds.value.length) {
        let title = ''
        let alertMsg = ''

        if (allItemsSelected.value) {
            title = 'CONFIRM BULK ACTION'
            alertMsg = 'This action will affect all ' + itemCount.value + ' items.<br><br>Are you sure you want to continue?'
        } else if (showArchive.value) {
            title = 'Unarchiving ' + selectedIds.value.length + ' item' + (selectedIds.value.length > 1 ? 's' : '')
            alertMsg = 'Are you sure?'
        } else {
            title = 'Archiving ' + selectedIds.value.length + ' item' + (selectedIds.value.length > 1 ? 's' : '')
            alertMsg = 'Are you sure?'
        }

        modalsStore.show({
            componentName: 'ConfirmModal',
            obj: {
                modalTitle: title,
                modalBody: `
                <p class="alert alert-danger">
                    ` + alertMsg + `
                </p>
            `,
                onConfirm: async () => {
                    try {
                        await api.post('api/ListingController/archive', {
                            tableName: tableName.value,
                            ids: selectedIds.value,
                            archive: !showArchive.value,
                            archiveAll: allItemsSelected.value
                        })

                        appStore.cacheNeedsClearing = true
                        viewStore.selectNone()
                        await viewStore.scheduleLoad()

                        if (
                            tableName.value === 'pages' ||
                            tableName.value === 'content'
                        ) {
                            // Refresh site tree pane
                            await siteTreeStore.init()

                            // After archiving content pageContentData must be refreshed so the archived content
                            // is not associated to its pages.
                            await pageContentDataStore.init()
                        }
                    } catch (err) {
                        // Handle error if needed
                    }
                }
            }
        })
    }
}
</script>

<template>
    <Button v-if="show && showText" @click="archiveSelectItems" class="d-flex align-items-center">
        <i class="me-2 bi-archive"/>
        {{ text }}
    </Button>

    <Button v-else-if="show" @click="archiveSelectItems">
        <i class="bi-archive"/>
        {{ text }}
    </Button>
</template>